import React, { useState } from 'react';

import {
    IConversationForAnalysisReport,
    IMSARMBTSReport,
} from '../../../../types/services.interfaces/mbts-ux';

import { AuditMatrix } from './AuditMatrix';
import { CoachingHabits } from './CoachingHabits';
import { CognitiveMastery } from './CognitiveMastery';
import { Feedback } from './Feedback';
import { InteractionLab } from './InteractionLab';
import { ReportHero } from './ReportHero';
import { ReportNavbar } from './ReportNavbar';
import { TViewMode } from './utils';

interface AnalysisReportV2Props {
    report: IMSARMBTSReport;
    conversations: IConversationForAnalysisReport[];
    scenarioId: string | null;
    isConsentLocked: boolean;
    openConsentDataGather: () => void;
};

export const AnalysisReportV2 = (props: AnalysisReportV2Props) => {
    const { report, conversations, scenarioId, isConsentLocked, openConsentDataGather } = props;
    const [viewMode, setViewMode] = useState<TViewMode>('narrative');
    const [isSpotlightActive, setIsSpotlightActive] = useState(false);

    const spotlightClass = (extra = '') =>
        `mbts_analysis_v2__spotlightable${extra ? ` ${extra}` : ''}`;

    const onSpotlightEnter = (e: React.MouseEvent<HTMLElement>) => {
        if (!isSpotlightActive) return;
        e.currentTarget.classList.add('mbts_analysis_v2__spotlightable--target');
    };

    const onSpotlightLeave = (e: React.MouseEvent<HTMLElement>) => {
        e.currentTarget.classList.remove('mbts_analysis_v2__spotlightable--target');
    };

    const spotlightHandlers = isSpotlightActive
        ? { onMouseEnter: onSpotlightEnter, onMouseLeave: onSpotlightLeave }
        : {};

    return (
        <div className={`mbts_analysis_v2${isSpotlightActive ? ' mbts_analysis_v2--spotlight' : ''}`}>
            <ReportNavbar
                report={report}
                isConsentLocked={isConsentLocked}
                isSpotlightActive={isSpotlightActive}
                onSpotlightChange={setIsSpotlightActive}
            />

            <div className="mbts_analysis_v2__main">
                <ReportHero
                    report={report}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    spotlightClassName={spotlightClass()}
                    spotlightHandlers={spotlightHandlers}
                />

                {viewMode === 'narrative' ? (
                    <div className="mbts_analysis_v2__narrative_flow">
                        <div className={spotlightClass()} {...spotlightHandlers}>
                            <CognitiveMastery
                                report={report}
                                isConsentLocked={isConsentLocked}
                                openConsentDataGather={openConsentDataGather}
                            />
                        </div>
                        <div className={spotlightClass()} {...spotlightHandlers}>
                            <InteractionLab
                                report={report}
                                conversations={conversations}
                                isConsentLocked={isConsentLocked}
                                openConsentDataGather={openConsentDataGather}
                            />
                        </div>
                        <div className={spotlightClass()} {...spotlightHandlers}>
                            <CoachingHabits
                                report={report}
                                isConsentLocked={isConsentLocked}
                                openConsentDataGather={openConsentDataGather}
                            />
                        </div>
                    </div>
                ) : (
                    <div className={spotlightClass()} {...spotlightHandlers}>
                        <AuditMatrix
                            report={report}
                            isConsentLocked={isConsentLocked}
                            openConsentDataGather={openConsentDataGather}
                        />
                    </div>
                )}

                <div className={spotlightClass()} {...spotlightHandlers}>
                    <Feedback
                        scenarioId={scenarioId}
                        analysisId={report.analysis_id}
                        isConsentLocked={isConsentLocked}
                        openConsentDataGather={openConsentDataGather}
                    />
                </div>
            </div>
        </div>
    );
};
